// 「下載全部」 for an album — W39.53.
//
// The row existed and looked exactly like the other two rows in the album's
// settings block, complete with a `›` chevron, but had no `onClick` at all: on a
// real 393×852 render of a 6-photo album, tapping it left the DOM byte-identical
// (21234 → 21234 characters). `PATCH`-and-`POST`-style handlers were wired in
// W3 PR2 for the other two rows; this one never was.
//
// Why this is not a loop of `a.click()`
//
// Browsers ask the user to allow "multiple downloads" and then silently block
// the ones after the first, so a 50-photo album would quietly save one file and
// report success. Fetching each file, checking the status, and saving the blob
// means a photo that 404s is *counted* rather than assumed fine — which is the
// difference between a progress line that ends in "done" and one that ends in
// "done, 3 could not be saved".
//
// Sequential, with a gap: one request in flight at a time, so a large album does
// not open 50 sockets, and the gaps give the browser's download manager time to
// register each file instead of collapsing them into one prompt.

import { mediaFileUrl } from "@/lib/api/album";

/** Milliseconds between files. Long enough for the download manager to notice. */
const GAP_MS = 250;

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** `photo_003_2026-10-02.png` — position first, so a sorted folder reads as a sequence. */
function fileName(index: number, mimeType: string, takenAt: string | null): string {
  const ext = mimeType.split("/")[1]?.replace("+xml", "").replace("jpeg", "jpg") ?? "bin";
  const day = takenAt ? takenAt.slice(0, 10) : null;
  const stem = `photo_${String(index + 1).padStart(3, "0")}`;
  return `${day ? `${stem}_${day}` : stem}.${ext || "bin"}`;
}

export interface DownloadOutcome {
  ok: number;
  failed: number;
}

export async function downloadAlbumPhotos(
  items: readonly { media: { id: string; mime_type: string }; taken_at: string | null }[],
  onProgress?: (done: number) => void,
): Promise<DownloadOutcome> {
  let ok = 0;
  let failed = 0;

  for (const [index, item] of items.entries()) {
    try {
      const res = await fetch(mediaFileUrl(item.media.id), { credentials: "include" });
      if (!res.ok) {
        failed += 1;
        onProgress?.(index + 1);
        continue;
      }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = fileName(index, item.media.mime_type, item.taken_at);
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoking immediately can cancel the save in some browsers; one turn of
      // the event loop plus the gap is enough for the download to take a
      // reference to the blob.
      await wait(GAP_MS);
      URL.revokeObjectURL(href);
      ok += 1;
    } catch {
      // A network error or a revoked blob counts as a failure rather than
      // aborting the run — one bad photo should not cost the other 49.
      failed += 1;
    }
    onProgress?.(index + 1);
  }

  return { ok, failed };
}
